import { GET, type IOMSTradeCapture, percentToBps, request, useConstant, useUserContext } from '@talos/kyoko';
import { useEffect } from 'react';
import type { CustomerTradeForm } from './types';

interface UseCustomerTradeDetailsParams {
  isOpen: boolean;
  tradeID?: string;
  updateForm: (payload: Partial<CustomerTradeForm>) => void;
}

export function useCustomerTradeDetails({ isOpen, tradeID, updateForm }: UseCustomerTradeDetailsParams) {
  const { orgApiEndpoint } = useUserContext();
  const tradeCaptureMap = useConstant(new Map<string, IOMSTradeCapture | null>());
  const isRequestingTradeDetails = isOpen && !!tradeID && !tradeCaptureMap.has(tradeID);

  useEffect(() => {
    if (!isOpen || !tradeID) {
      return;
    }

    const requestedTradeID = tradeID;
    let isCurrentRequest = true;
    request<{ data: [IOMSTradeCapture] }>(GET, `${orgApiEndpoint}/trades?TradeID=${requestedTradeID}`)
      .then(res => {
        if (!isCurrentRequest) {
          return;
        }

        const trade = res.data.at(0);
        if (trade) {
          tradeCaptureMap.set(requestedTradeID, trade);
          updateForm({
            salesCommission: percentToBps(trade.SalesCommission),
            riskSubAccount: trade.SubAccount,
          });
        } else {
          tradeCaptureMap.set(requestedTradeID, null);
          updateForm({});
        }
      })
      .catch(() => {
        // Keep row values usable when supplemental detail data is unavailable.
        if (!isCurrentRequest) {
          return;
        }
        tradeCaptureMap.set(requestedTradeID, null);
        updateForm({});
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [isOpen, orgApiEndpoint, tradeCaptureMap, tradeID, updateForm]);

  useEffect(() => {
    if (!isOpen) {
      tradeCaptureMap.clear();
    }
  }, [isOpen, tradeCaptureMap]);

  return { isRequestingTradeDetails };
}
